import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material'

import {
  ColumnDef,
  PaginationState,
  Row,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React, { useMemo, useState } from 'react'
import Text from '../Text'
import Pagination from './Pagination'

interface IDataTableProps<TData = any> {
  columns: Array<ColumnDef<TData, any>>
  data: TData[]
  isShowFooter?: boolean
  heading?: string | React.ReactNode
  getRowId?:
    | ((
        originalRow: any,
        index: number,
        parent?: Row<any> | undefined
      ) => string)
    | undefined
  additionalComponent?: React.ReactNode
  pageSize?: number
  pageIndex?: number
  totalRows?: number
  onPageChange?: (state: PaginationState) => void
  isShowPagination?: boolean
}

const DataTable = ({
  columns,
  data,
  heading,
  getRowId,
  isShowFooter = false,
  additionalComponent,
  pageSize: defaultPageSize = 20,
  pageIndex: defaultPageIndex = 0,
  totalRows = 0,
  onPageChange,
  isShowPagination = false,
}: IDataTableProps) => {
  const [{ pageSize, pageIndex }, setPagination] = useState<PaginationState>({
    pageIndex: defaultPageIndex,
    pageSize: defaultPageSize,
  })

  const paginationState = {
    pageIndex,
    pageSize,
  }

  const pageCount = useMemo(
    () => (totalRows ? Math.ceil(totalRows / pageSize) : -1),
    [totalRows, pageSize]
  )

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    state: {
      pagination: paginationState,
    },
    onPaginationChange: setPagination,
    pageCount,
    enableColumnResizing: false,
    defaultColumn: {
      size: 150,
      maxSize: Number.MAX_SAFE_INTEGER,
      minSize: 20,
    },
    manualPagination: true,
  })

  const { getHeaderGroups, getRowModel, getFooterGroups } = table
  return (
    <Box
      sx={() => ({
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      })}
    >
      <Grid container justifyContent="space-between" alignItems="center">
        {heading && (
          <Grid item>
            <Text variant="h6" fontWeight="bold">
              {heading}
            </Text>
          </Grid>
        )}
        {additionalComponent && (
          <Grid alignSelf="flex-end" item>
            {additionalComponent}
          </Grid>
        )}
      </Grid>
      <TableContainer
        sx={{
          overflowX: 'auto',
          boxShadow: 1,
          borderRadius: 2,
          msOverflowY: 'scroll',
          maxHeight: 1500,
        }}
      >
        <Table>
          <TableHead
            sx={() => ({
              background: '#36304a',
              '& > tr > th': {
                padding: 1.5,
              },
            })}
          >
            {getHeaderGroups().map(g => {
              return (
                <TableRow key={g.id}>
                  {g.headers.map(h => {
                    return (
                      <TableCell
                        key={h.id}
                        sx={theme => ({
                          color: theme.palette.common.white,
                          borderBottom: 'none',
                          fontFamily: 'Nunito',
                          fontWeight: 'bold',
                          opacity: 0.8,
                          width: h.column.getSize()
                            ? h.column.getSize()
                            : 'auto',
                        })}
                      >
                        {h.isPlaceholder
                          ? null
                          : flexRender(
                              h.column.columnDef.header,
                              h.getContext()
                            )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableHead>
          <TableBody>
            {getRowModel().rows.map((row, index) => {
              const isLastRow = index === getRowModel().rows.length - 1
              return (
                <TableRow
                  sx={() => ({
                    '& > td': {
                      borderBottom: isLastRow
                        ? 'none'
                        : '1px solid rgba(224, 224, 224, 1)',
                    },
                  })}
                  key={row.id}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      sx={{
                        fontFamily: 'Nunito',
                        fontWeight: 'bold',
                        width: cell.column.getSize()
                          ? cell.column.getSize()
                          : 'auto',
                        padding: 1.5,
                      }}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
          {isShowFooter && (
            <TableFooter>
              {getFooterGroups().map(footerGroup => (
                <TableRow key={footerGroup.id}>
                  {footerGroup.headers.map(header => (
                    <TableCell key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      {isShowPagination && (
        <Pagination
          table={table}
          paginationState={paginationState}
          totalRows={totalRows}
          onPageChange={onPageChange}
        />
      )}
    </Box>
  )
}

export default DataTable
